import { Box, Typography, Alert, Button, TextField } from "@mui/material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import themeColors from "../theme";
import Logo from "../components/Logo";

export default function EmailNotVerifiedPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { teal, darkTeal, lightGray } = themeColors;

    // Get email from navigation state if available
    const initialEmail = location.state?.email || "";

    const [email, setEmail] = useState(initialEmail);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const handleResendVerification = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (!email) {
            setError("Please enter your email address");
            setLoading(false);
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/resend-verification`, { email });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to send verification email. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: lightGray,
                px: 2,
            }}
        >
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Logo />
            </Box>

            <Box
                sx={{
                    width: "100%",
                    maxWidth: 470,
                    backgroundColor: "#fff",
                    p: 5,
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center",
                }}
            >
                <MailOutlineIcon sx={{ fontSize: 64, color: teal, mb: 2 }} />

                <Typography variant="h5" fontWeight="600" sx={{ color: "#1D3557", mb: 2 }}>
                    Email Verification Required
                </Typography>

                {success ? (
                    <>
                        <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                            Verification email sent! Please check your inbox and click the link to verify your email address.
                        </Alert>
                        <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                            Didn't receive the email? Check your spam folder or request another one below.
                        </Typography>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setSuccess(false)}
                            sx={{
                                py: 1.5,
                                borderColor: teal,
                                color: teal,
                                '&:hover': { borderColor: darkTeal, backgroundColor: 'rgba(0, 127, 126, 0.04)' },
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                mb: 2
                            }}
                        >
                            Send Another Verification Email
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate("/login")}
                            sx={{
                                py: 1.5,
                                backgroundColor: teal,
                                '&:hover': { backgroundColor: darkTeal },
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.95rem"
                            }}
                        >
                            Back to Login
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                            Your email address has not been verified yet. Please verify your email to access your account.
                        </Typography>

                        <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                            Enter your email address below and we'll send you a new verification link.
                        </Alert>

                        <Box component="form" onSubmit={handleResendVerification}>
                            <TextField
                                fullWidth
                                margin="normal"
                                placeholder="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                sx={{ mb: 2 }}
                                InputLabelProps={{ shrink: false }}
                            />

                            {error && (
                                <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                                    {error}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading || !email}
                                sx={{
                                    py: 1.5,
                                    backgroundColor: teal,
                                    '&:hover': { backgroundColor: darkTeal },
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    mb: 2
                                }}
                            >
                                {loading ? "Sending..." : "Send Verification Email"}
                            </Button>
                        </Box>

                        <Box sx={{ borderTop: "1px solid #E0E0E0", pt: 2 }}>
                            <Typography variant="body2" sx={{ color: "#5A6C7D" }}>
                                Already verified?{" "}
                                <Button
                                    variant="text"
                                    sx={{ color: teal, textTransform: "none", p: 0, minWidth: "auto", fontWeight: 500 }}
                                    onClick={() => navigate("/login")}
                                >
                                    Sign in
                                </Button>
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}
